import { useRef } from "react";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { LANDING } from "@/constants/testIds";
import { scrollToId, DEMO_MAILTO } from "@/lib/scroll";

const HERO_IMG =
    "https://images.pexels.com/photos/27402391/pexels-photo-27402391.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=1920";

// Masked line-by-line reveal
const lineParent = {
    hidden: {},
    show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const lineChild = {
    hidden: { y: "110%" },
    show: {
        y: "0%",
        transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
    },
};

const Line = ({ children }) => (
    <span className="block overflow-hidden">
        <motion.span variants={lineChild} className="block">
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
    const copyY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
    const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

    return (
        <section
            ref={ref}
            className="relative min-h-screen w-full bg-white overflow-hidden pt-24 md:pt-28"
        >
            <div
                className="absolute inset-0 opacity-[0.35] pointer-events-none"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(9,21,36,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(9,21,36,0.05) 1px, transparent 1px)",
                    backgroundSize: "64px 64px",
                }}
            />

            <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-12 pb-20 md:pt-16 md:pb-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                    {/* Copy column */}
                    <motion.div style={{ y: copyY }} className="lg:col-span-7">
                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#091524]/15 bg-[#F0F4F8] rounded-sm mb-8"
                        >
                            <ShieldCheck size={14} className="text-[#0052CC]" strokeWidth={2.5} />
                            <span className="label-eyebrow text-[#091524]/70">
                                Enterprise Safety Reporting
                            </span>
                        </motion.div>

                        <motion.h1
                            variants={lineParent}
                            initial="hidden"
                            animate="show"
                            className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-extrabold text-[#091524] leading-[0.95] tracking-tight"
                        >
                            <Line>Safety data</Line>
                            <Line>
                                that <span className="text-[#0052CC]">works</span> as
                            </Line>
                            <Line>hard as you do.</Line>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.65 }}
                            className="mt-8 text-lg md:text-xl text-[#475569] max-w-2xl leading-relaxed"
                        >
                            Apex Pro is a secure,{" "}
                            <strong className="text-[#091524] font-bold">
                                cloud-based safety reporting platform with offline functionality
                            </strong>{" "}
                            &mdash; engineered to eliminate fragmented data, manual processes, and
                            inconsistent reporting across multi-site operations.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.8 }}
                            className="mt-10 flex flex-col sm:flex-row gap-4"
                        >
                            <a
                                data-testid={LANDING.heroRequestDemoBtn}
                                href={DEMO_MAILTO}
                                className="group inline-flex items-center justify-center gap-2 bg-[#091524] hover:bg-[#0052CC] text-white px-8 py-4 rounded-sm font-semibold transition-all duration-300 hover:-translate-y-0.5"
                            >
                                Request a Demo
                                <ArrowRight
                                    size={18}
                                    className="group-hover:translate-x-1 transition-transform"
                                />
                            </a>
                            <button
                                data-testid={LANDING.heroLearnMoreBtn}
                                onClick={() => scrollToId("features")}
                                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-[#091524] text-[#091524] hover:bg-[#091524] hover:text-white px-8 py-4 rounded-sm font-semibold transition-all duration-300"
                            >
                                Explore Features
                            </button>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.95 }}
                            className="mt-14 grid grid-cols-3 gap-6 max-w-2xl"
                        >
                            {[
                                { v: "3", l: "Case Categories" },
                                { v: "100%", l: "Offline Ready" },
                                { v: "Multi", l: "Site Benchmarking" },
                            ].map((s) => (
                                <div key={s.l} className="border-t-2 border-[#0052CC] pt-4">
                                    <div className="font-display text-3xl md:text-4xl font-extrabold text-[#091524]">
                                        {s.v}
                                    </div>
                                    <div className="mt-1 label-eyebrow text-[#091524]/50 text-[0.65rem]">
                                        {s.l}
                                    </div>
                                </div>
                            ))}
                        </motion.div>
                    </motion.div>

                    {/* Image column — clipped frame with parallax */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.9, delay: 0.4 }}
                        className="lg:col-span-5 relative"
                    >
                        <div className="relative rounded-sm overflow-hidden border border-[#091524]/10 shadow-[0_24px_60px_rgba(9,21,36,0.15)] h-[420px] md:h-[560px]">
                            <motion.img
                                src={HERO_IMG}
                                alt="Aerial view of container ship"
                                style={{ y: imgY, scale: imgScale }}
                                className="absolute inset-0 w-full h-[130%] object-cover -top-[15%]"
                            />
                            <motion.div
                                style={{ opacity: overlayOpacity }}
                                className="absolute inset-0 bg-gradient-to-t from-[#091524] via-[#091524]/30 to-transparent"
                            />
                            <div className="absolute bottom-0 inset-x-0 p-6 md:p-8 text-white z-10">
                                <div className="label-eyebrow text-white/70 mb-2">
                                    Built for the field
                                </div>
                                <div className="font-display text-xl md:text-2xl font-bold leading-tight">
                                    From bridge to boardroom &mdash; one source of truth.
                                </div>
                            </div>
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 1.1 }}
                            className="hidden md:block absolute -left-8 top-10 bg-white border border-[#091524]/10 rounded-sm p-4 shadow-[0_12px_40px_rgba(9,21,36,0.10)] max-w-[220px] z-20"
                        >
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                <span className="label-eyebrow text-[#091524]/60">Live</span>
                            </div>
                            <div className="mt-2 font-display text-2xl font-extrabold text-[#091524]">
                                24 Open
                            </div>
                            <div className="text-xs text-[#475569] mt-1">
                                Actions across 4 sites
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
